import { GroupedOptions, SelectOption, SingleOption, explodeOptions, isSingleOption } from '@frontend/basic-forms';
import { useLocale } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ObjectSelectInputPropsV2 } from './object-select-input-v2.component';
import { mapResultSetToMappedOptions, mapResultSetToOptions, onQueryParamChange } from './object-select-mappers';

export { mapResultSetToMappedOptions, mapResultSetToOptions, onQueryParamChange } from './object-select-mappers';

interface ViewProps<T extends { id: string }> {
    options: SelectOption<T>[];
    value?: T | null;
    onScrollToBottom: () => void;
    search: (value: string) => void;
    onSelect: (option: SingleOption<T> | null) => void;
    overwriteDisabled?: boolean;
    hide: boolean;
}

const pageSize = '100';

const useObjectSelectInputV2 = <T extends { id: string }, D extends string | number>(props: ObjectSelectInputPropsV2<T, D>): ViewProps<T> => {
    const { locale } = useLocale();
    const params = useParams();
    const { store } = useAccountRepository();
    const [urlOverwrite, changeUrlOverwrite] = useState<T | null>(null);
    const [cache, changeCache] = useState<T[]>([]);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [count, changeCount] = useState<number>(0);
    const [index, changeIndex] = useState<number>(0);
    const [options, changeOptions] = useState<SelectOption<T>[]>([]);
    const [mappedOptions, changeMappedOptions] = useState<GroupedOptions<T>[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const value: T | null = useMemo(() => {
        if (props.isClearable && props.value === null && props.idValue === null) {
            props.onChange && props.onChange(null);
            return null;
        }
        const explodedOptions = explodeOptions(options);
        if (explodedOptions.length === 1 && props.required) {
            const found = cache.find((o) => o.id === explodedOptions[0].value);
            if (!found) return null;
            if (props.onChange && props.value?.id !== found.id) props.onChange(found);
        }
        return props.value ? (props.value.id === props.idValue ? props.value : (cache.find((o) => o.id === props.idValue) ?? null)) : null;
    }, [props.value, props.idValue, options]);

    useEffect(() => {
        const param = searchParams.get(props.useQueryParam?.param ?? '');
        if (!urlOverwrite) {
            if (!param && !props.value) return;
            onQueryParamChange(value?.id ?? null, props.useQueryParam?.param ?? '', setSearchParams);
        }
    }, [value]);

    useEffect(() => {
        if (!props.idValue) return;
        if (props.singleFetch && options && options.length > 0 && !explodeOptions(options).find((o) => o.value == props.idValue)) {
            props.singleFetch(props.idValue).then((res) => {
                changeOptions((prevOptions) => {
                    const exploded = explodeOptions(prevOptions);
                    const found = exploded.find((o) => o.value === res.id);
                    if (found) return prevOptions;
                    else return [...prevOptions, { value: res.id, label: props.itemLabel(res) }];
                });
                changeCache((prevCache) => [...prevCache, res]);
            });
        }
    }, [props.idValue, options, locale]);

    const onSelect = (option: SingleOption<T> | null) => {
        if (option && option.metadata) {
            props.onChange && props.onChange(option.metadata);
        } else {
            const object = cache.find((o) => o.id === option?.value);
            props.onChange && props.onChange(object ?? null);
        }
    };

    useEffect(() => {
        if (props.idValue && props.value == undefined && value == null && props.onChange) {
            const found = cache.find((o) => o.id === props.idValue);
            if (found) props.onChange(found);
        }
    }, [options]);

    useEffect(() => {
        if (props.useUrlOverwrite && !props.disableFetch) {
            const param = params[props.useUrlOverwrite.param];
            if (param !== undefined)
                props.useUrlOverwrite.fetch(param).then((result) => {
                    changeUrlOverwrite(result);
                    props.onChange && props.onChange(result);
                });
        }
    }, [props.useUrlOverwrite]);

    useEffect(() => {
        const queryParam = searchParams.get(props.useQueryParam?.param ?? '');
        if (props.useQueryParam && value === null && queryParam !== null && !props.disableFetch) {
            props.useQueryParam.fetch(queryParam).then((result) => {
                props.onChange && props.onChange(result);
            });
        }
    }, [onQueryParamChange]);

    //TODO: this usePrevious is used because the useEffect triggered incorrectly (but should be investigated and fixed properly later when we have the time)
    const previous = usePrevious({ params: props.queryParams, index: index, account: store.currentAccount });
    useEffect(() => {
        if (!store.currentAccount) return;
        if (props.disableFetch) return;
        if (
            ((previous == undefined ||
                JSON.stringify(previous.params) != JSON.stringify(props.queryParams) ||
                previous.index != index ||
                previous.account != store.currentAccount) &&
                store.currentAccount) ||
            !props.value
        ) {
            props.fetch({ index: index.toString(), size: pageSize, account_id: store.currentAccount, ...props.queryParams }).then((result) => {
                changeCount(result.count);
                if (index == 0) {
                    if (!props.mapOptions) {
                        changeOptions(mapResultSetToOptions(result.results, props.itemLabel));
                        changeCache(result.results);
                    } else {
                        changeMappedOptions(mapResultSetToMappedOptions(result.results, props.mapOptions, props.itemLabel));
                    }
                } else {
                    if (!props.mapOptions) {
                        changeOptions((options ?? []).concat(mapResultSetToOptions(result.results, props.itemLabel)));
                        changeCache((cache ?? []).concat(result.results));
                    } else {
                        changeMappedOptions((mappedOptions ?? []).concat(mapResultSetToMappedOptions(result.results, props.mapOptions, props.itemLabel)));
                        changeCache((cache ?? []).concat(result.results));
                    }
                }
            });
        }
    }, [index, props.queryParams, store.currentAccount, locale]);

    useEffect(() => {
        if (props.mapOptions) {
            mapResultSetToMappedOptions(cache, props.mapOptions, props.itemLabel);
        } else {
            changeOptions(mapResultSetToOptions(cache, props.itemLabel));
        }
    }, [props.itemLabel, locale]);

    const search = (value: string) => {
        if (props.disableFetch) return;
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            props.fetch({ search: value, index: '0', size: pageSize, account_id: store.currentAccount, ...props.queryParams }).then((result) => {
                changeOptions(mapResultSetToOptions(result.results, props.itemLabel));
                changeCache(result.results);
            });
        }, 500);
    };

    const scrollBottomVariableRef = useRef({ count, pageSize, index });
    useEffect(() => {
        scrollBottomVariableRef.current = { count, pageSize, index };
    }, [count, pageSize, index]);
    const onScrollToBottom = () => {
        const { count, pageSize, index } = scrollBottomVariableRef.current;
        if (count / parseInt(pageSize) > 1 && parseInt(pageSize) * index < count) {
            changeIndex(index + 1);
        }
    };

    const opt = useMemo(() => {
        if (props.subset) {
            if (props.subset.length === 0) return [];
            if (props.mapOptions) {
                const result: { label: string; options: { value: string; label: string }[] }[] = [];
                mappedOptions.forEach((o) => {
                    const current: { label: string; options: { value: string; label: string }[] } = { label: o.label, options: [] };
                    o.options.filter((op) => props.subset!.includes(op.value)).forEach((op) => current.options.push(op));
                    if (current.options.length > 0) result.push(current);
                });
                return result;
            } else {
                return options.filter((o) => {
                    if (isSingleOption(o)) {
                        return props.subset!.includes(o.value);
                    }
                    return o.options.filter((op) => props.subset!.includes(op.value)).length > 0;
                });
            }
        } else return props.mapOptions ? mappedOptions : options;
    }, [props.mapOptions, props.subset, options, mappedOptions]);

    return {
        options: opt,
        value,
        onScrollToBottom,
        search,
        onSelect,
        overwriteDisabled:
            urlOverwrite !== null || (!props.hideOnOneOption && options.length === 1 && !props.isClearable && props.required) || props.disableFetch,
        hide: props.hidden === true || (props.hideOnOneOption === true && options.length <= 1 && !props.isClearable)
    };
};

export default useObjectSelectInputV2;

export function usePrevious<T>(value: T) {
    const ref = useRef<T>(null);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}
